/**
 * 직원 삭제 Mutations Hook (V3 - 단일 삭제 아키텍처)
 *
 * V3 변경사항 (단일 삭제 아키텍처):
 * - deleteEmployee: 단일 직원 삭제 (권장)
 * - deleteEmployees: 여러 명 삭제 시 한 명씩 순차 호출
 * - 타임아웃/internal 에러 문제 해결
 * - 진행 상태 콜백 지원 (onProgress)
 *
 * V2 변경사항:
 * - 백엔드 V2 안정화에 맞춰 에러 처리 강화
 * - 타입 안전성 강화 (httpsCallable 제네릭 명시)
 *
 * 직원 삭제 관련 Cloud Functions 호출을 위한 커스텀 훅입니다.
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

// ============================================
// 타입 정의
// ============================================

/**
 * V3: 단일 직원 삭제 요청 데이터
 */
export interface DeleteEmployeeRequest {
  employeeId: string;
}

/**
 * V3: 단일 직원 삭제 응답 데이터
 */
export interface DeleteEmployeeResult {
  success: boolean;
  message?: string;
}

/**
 * 복수 직원 삭제 결과 (집계)
 */
export interface DeleteEmployeesResult {
  requestedCount: number;
  deletedCount: number;
  failures: Array<{
    userId: string;
    reason: string;
  }>;
}

/**
 * V3: 삭제 진행 상태 콜백
 */
export interface DeleteProgress {
  current: number;
  total: number;
  currentUserId: string;
}

// ============================================
// 직원 삭제 Mutations Hook
// ============================================

/**
 * 직원 삭제 Mutations Hook (V3)
 */
export function useEmployeeDeleteMutations() {
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * V3: 단일 직원 삭제 (권장)
   *
   * 한 명의 직원을 삭제합니다.
   * - 이미 삭제된 직원도 success=true 반환
   * - 인증/권한 에러만 throw
   *
   * @param employeeId 삭제할 직원 ID
   * @returns 삭제 결과
   */
  const deleteEmployee = async (employeeId: string): Promise<DeleteEmployeeResult> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    if (!employeeId || employeeId.trim() === '') {
      throw new Error('삭제할 직원 ID가 필요합니다.');
    }

    try {
      const deleteEmployeeSafelyFn = httpsCallable<DeleteEmployeeRequest, DeleteEmployeeResult>(
        functions,
        'deleteEmployeeSafely'
      );

      const result = await deleteEmployeeSafelyFn({ employeeId: employeeId.trim() });

      return result.data;

    } catch (error: any) {
      const errorMessage =
        error.details?.message ||
        error.message ||
        '직원 삭제 중 오류가 발생했습니다.';

      throw new Error(errorMessage);
    }
  };

  /**
   * V3: 여러 직원 삭제 - 한 명씩 순차 호출 (권장)
   *
   * 여러 명의 직원을 한 명씩 순차적으로 삭제합니다.
   * - 타임아웃/internal 에러 문제 해결
   * - 진행 상태 콜백 지원
   * - 한 명 실패해도 나머지 계속 처리
   *
   * @param userIds 삭제할 직원 ID 배열
   * @param onProgress 진행 상태 콜백 (선택)
   * @returns 최종 집계 결과
   */
  const deleteEmployees = async (
    userIds: string[],
    onProgress?: (progress: DeleteProgress) => void
  ): Promise<DeleteEmployeesResult> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    if (userIds.length === 0) {
      throw new Error('삭제할 직원이 선택되지 않았습니다.');
    }

    setIsDeleting(true);

    // 최종 결과 초기화
    const result: DeleteEmployeesResult = {
      requestedCount: userIds.length,
      deletedCount: 0,
      failures: [],
    };

    try {
      // 한 명씩 순차 삭제
      for (let i = 0; i < userIds.length; i++) {
        const userId = userIds[i];

        // 진행 상태 콜백
        if (onProgress) {
          onProgress({
            current: i + 1,
            total: userIds.length,
            currentUserId: userId,
          });
        }

        try {
          const deleteResult = await deleteEmployee(userId);

          if (deleteResult.success) {
            result.deletedCount++;
          } else {
            result.failures.push({
              userId,
              reason: deleteResult.message || '알 수 없는 오류',
            });
          }

        } catch (error: any) {
          // 인증/권한 에러면 즉시 중단
          if (error.code === 'unauthenticated' || error.code === 'permission-denied') {
            console.error(`❌ 인증/권한 에러 - 중단: ${error.message}`);
            throw error;
          }

          // 그 외 에러는 실패로 기록하고 계속
          result.failures.push({
            userId,
            reason: error.message || '알 수 없는 오류',
          });
        }
      }

      return result;

    } catch (error: any) {
      console.error('❌ V3 deleteEmployees 에러:', error);
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * 레거시: 여러 직원 삭제 (단일 Cloud Function 호출)
   *
   * ⚠️ V3에서는 deleteEmployees (한 명씩 순차 호출) 사용 권장
   */
  const deleteEmployeesLegacy = async (
    userIds: string[]
  ): Promise<DeleteEmployeesResult> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    if (userIds.length === 0) {
      throw new Error('삭제할 직원이 선택되지 않았습니다.');
    }

    setIsDeleting(true);
    try {
      const deleteEmployeesSafelyFn = httpsCallable<{ userIds: string[] }, DeleteEmployeesResult>(
        functions,
        'deleteEmployeesSafely'
      );

      const result = await deleteEmployeesSafelyFn({ userIds });
      return result.data;

    } catch (error: any) {
      console.error('❌ 레거시 삭제 실패:', error);
      throw new Error(error.message || '직원 삭제 중 오류가 발생했습니다.');
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteEmployee,         // V3: 단일 삭제 (권장)
    deleteEmployees,        // V3: 복수 삭제 - 한 명씩 순차 (권장)
    deleteEmployeesLegacy,  // 레거시: 복수 삭제 - 단일 호출
    isDeleting,
  };
}
